import { useCallback, useEffect, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Landmark, ShieldCheck } from '@/components/ui/icons'
import type { z } from 'zod'
import { Button } from '@/components/ui/Button'
import { Select, type SelectOption } from '@/components/ui/Select'
import { TextInput } from '@/components/ui/TextInput'
import { ConfirmedField } from '@/components/ui/ConfirmedField'
import { SV_BANKS, getSvBankLabel } from '@/lib/data/sv-banks'
import { FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, SPACING, RADIUS, FORM_FIELD_GAP } from '@/lib/theme/tokens'
import { notification, NotificationType } from '@/lib/utils/haptics'
import {
  formatBankAccountInput,
  formatPayoutDocumentInput,
  organizationPayoutAccountSchema,
  personalPayoutAccountSchema,
  type PayoutAccountFormValues,
  type PayoutDocumentType,
} from '@/lib/validations/payout.schema'

type PayoutAccountFormMode = 'personal' | 'organization'

interface PayoutAccountFormProps {
  mode: PayoutAccountFormMode
  initialHolderName?: string
  initialDocumentNumber?: string
  /**
   * Where the holder's name and document came from, when they came from
   * somewhere. Supplying it turns those two fields from inputs into confirmable
   * summaries — the person is told what we already hold instead of being asked
   * to type it again. Omit it and they stay editable, which is what the
   * organization flow needs while an agency's NIT is still self-declared.
   */
  identityProvenance?: string
  /**
   * Provenance for the holder's NAME specifically, when it differs from the
   * document's.
   *
   * One flag used to govern both fields, which was wrong in both directions:
   * the official DUI-lookup path approves from the number and the date of birth
   * and never writes a name, so falling back to the profile name either
   * labelled a self-typed value "De tu DUI verificado" — a claim the app cannot
   * make — or, by withholding the flag, degraded a registry-confirmed DUI back
   * into a free-text box. They are two different facts and they get two props.
   */
  holderNameProvenance?: string
  organizationName?: string
  submitting?: boolean
  submitLabel: string
  testIDPrefix: string
  onSubmit: (values: PayoutAccountFormValues) => Promise<void>
}

type FieldErrors = Partial<
  Record<
    'accountType' | 'bankCode' | 'accountNumber' | 'accountHolderName' | 'documentNumber',
    string
  >
>

const BANK_OPTIONS: SelectOption<string>[] = SV_BANKS.map((bank) => ({
  value: bank.value,
  label: bank.label,
}))

function issuesToErrors(
  issues: z.ZodIssue[],
  translate: ReturnType<typeof useTranslation>['t'],
): FieldErrors {
  const next: FieldErrors = {}
  for (const issue of issues) {
    const field = issue.path[0]
    if (typeof field !== 'string') continue
    next[field as keyof FieldErrors] = translate(issue.message)
  }
  return next
}

export function PayoutAccountForm({
  mode,
  initialHolderName = '',
  initialDocumentNumber = '',
  identityProvenance,
  holderNameProvenance,
  organizationName,
  submitting = false,
  submitLabel,
  testIDPrefix,
  onSubmit,
}: PayoutAccountFormProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const [accountType, setAccountType] = useState<'savings' | 'checking'>(
    mode === 'organization' ? 'checking' : 'savings',
  )
  const [bankCode, setBankCode] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [holderName, setHolderName] = useState(initialHolderName)
  // Derived from who is being paid, not chosen by the person being paid. An
  // agency has its own NIT; a natural person's DUI *is* their NIT, so there is
  // no second option to offer them.
  const documentType: PayoutDocumentType = mode === 'organization' ? 'nit' : 'dui'
  const [documentNumber, setDocumentNumber] = useState(
    initialDocumentNumber ? formatPayoutDocumentInput(initialDocumentNumber, documentType) : '',
  )
  const [errors, setErrors] = useState<FieldErrors>({})

  useEffect(() => {
    setHolderName((current) => current || initialHolderName)
  }, [initialHolderName])

  useEffect(() => {
    setDocumentNumber((current) =>
      current || initialDocumentNumber
        ? formatPayoutDocumentInput(current || initialDocumentNumber, documentType)
        : '',
    )
  }, [documentType, initialDocumentNumber])

  const accountTypeOptions: SelectOption<'savings' | 'checking'>[] = useMemo(
    () => [
      { value: 'savings', label: t('payouts.account_savings') },
      { value: 'checking', label: t('payouts.account_checking') },
    ],
    [t],
  )

  const rawValues = useMemo(
    () => ({
      accountType,
      bankCode,
      accountNumber,
      accountHolderName: holderName,
      documentType,
      documentNumber,
    }),
    [accountType, bankCode, accountNumber, holderName, documentType, documentNumber],
  )

  const schema =
    mode === 'organization' ? organizationPayoutAccountSchema : personalPayoutAccountSchema
  const parsed = schema.safeParse(rawValues)
  const canSubmit = parsed.success && !submitting

  const clearError = useCallback((field: keyof FieldErrors) => {
    setErrors((current) => ({ ...current, [field]: undefined }))
  }, [])

  const handleSubmit = useCallback(async () => {
    const result = schema.safeParse(rawValues)
    if (!result.success) {
      setErrors(issuesToErrors(result.error.issues, t))
      void notification(NotificationType.Error)
      return
    }
    setErrors({})
    await onSubmit(result.data)
  }, [onSubmit, rawValues, schema, t])

  const documentHelper =
    mode === 'organization' ? t('payouts.document_helper_org') : t('payouts.document_helper_dui')

  return (
    // No `gap` here on purpose. Select / TextInput each reserve FORM_FIELD_GAP
    // below themselves (see tokens.ts), so a container gap stacked on top of
    // that and gave 56pt between field rows while the blocks that are NOT
    // fields — the cards, the segmented group, the CTA — only got 24pt. Every
    // block below therefore owns the same FORM_FIELD_GAP and the form is on
    // one scale.
    <View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: SPACING.sm,
          backgroundColor: isDark ? colors.surface : '#FFFFFF',
          padding: SPACING.md,
          borderRadius: RADIUS.md,
          borderWidth: 1,
          borderColor: colors.border,
          marginBottom: FORM_FIELD_GAP,
        }}
      >
        <View
          style={{
            width: 36,
            height: 36,
            borderRadius: RADIUS.full,
            backgroundColor: `${colors.success}12`,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ShieldCheck size={18} color={colors.success} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 14,
              lineHeight: 20,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
          >
            {t('payouts.secure_title')}
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontSize: 13,
              lineHeight: 19,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {mode === 'organization'
              ? // validate-payout-organization-flow.mjs requires the explicit
                // fallback form at this call site — keep defaultValue here.
                t('org_payouts.onboarding_intro', {
                  defaultValue:
                    'La cuenta debe estar a nombre de la razon social. Guardamos solo los ultimos 4 digitos.',
                })
              : t('payouts.onboarding_intro')}
          </Text>
        </View>
      </View>

      {organizationName ? (
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: SPACING.sm,
            padding: SPACING.md,
            borderRadius: RADIUS.md,
            backgroundColor: colors.surfaceSubtle,
            borderWidth: 1,
            borderColor: colors.border,
            marginBottom: FORM_FIELD_GAP,
          }}
        >
          <Landmark size={18} color={colors.textSecondary} />
          <Text
            style={{
              flex: 1,
              fontSize: 13,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
            }}
            numberOfLines={1}
          >
            {organizationName}
          </Text>
        </View>
      ) : null}

      <Select
        label={t('payouts.bank')}
        value={bankCode}
        options={BANK_OPTIONS}
        onChange={(value) => {
          setBankCode(value)
          clearError('bankCode')
        }}
        placeholder={t('payouts.bank_placeholder')}
        searchable
        sheetTitle={t('payouts.bank')}
        error={errors.bankCode}
        testID={`${testIDPrefix}.bank`}
      />

      <Select
        label={t('payouts.account_type')}
        value={accountType}
        options={accountTypeOptions}
        onChange={(value) => {
          setAccountType(value)
          clearError('accountType')
        }}
        testID={`${testIDPrefix}.account-type`}
      />

      <TextInput
        label={t('payouts.account_number')}
        value={accountNumber}
        onChangeText={(value) => {
          setAccountNumber(formatBankAccountInput(value))
          clearError('accountNumber')
        }}
        placeholder={t('payouts.account_number_placeholder')}
        keyboardType="number-pad"
        inputMode="numeric"
        autoComplete="off"
        autoCapitalize="none"
        error={errors.accountNumber}
        testID={`${testIDPrefix}.account-number`}
      />

      {holderNameProvenance ? (
        <View style={{ marginBottom: FORM_FIELD_GAP }}>
          <ConfirmedField
            label={t('payouts.holder_name')}
            value={holderName}
            source={holderNameProvenance}
            testID={`${testIDPrefix}.holder-name-confirmed`}
            colors={colors}
          />
        </View>
      ) : (
        <TextInput
          label={t('payouts.holder_name')}
          value={holderName}
          onChangeText={(value) => {
            setHolderName(value)
            clearError('accountHolderName')
          }}
          placeholder={
            mode === 'organization'
              ? t('payouts.holder_name_org_placeholder')
              : t('payouts.holder_name_placeholder')
          }
          autoCapitalize="words"
          autoComplete="name"
          error={errors.accountHolderName}
          testID={`${testIDPrefix}.holder-name`}
        />
      )}

      {/* The "DUI o NIT" segmented control used to sit here. It asked a host to
          classify their own document, which for a natural person in El
          Salvador is not a classification — the DUI is the tax number. Tapping
          the other option also cleared the field, so the only thing the
          control could do was cost someone the number they had just typed. */}

      {identityProvenance ? (
        <View style={{ marginBottom: FORM_FIELD_GAP }}>
          <ConfirmedField
            label={t('payouts.document_number')}
            value={documentNumber}
            source={identityProvenance}
            testID={`${testIDPrefix}.document-number-confirmed`}
            colors={colors}
          />
        </View>
      ) : (
        <View>
          <TextInput
            label={mode === 'organization' ? t('org.tax_id') : t('payouts.document_number')}
            value={documentNumber}
            onChangeText={(value) => {
              setDocumentNumber(formatPayoutDocumentInput(value, documentType))
              clearError('documentNumber')
            }}
            placeholder={
              documentType === 'nit'
                ? t('org.tax_id_placeholder')
                : t('payouts.document_number_placeholder')
            }
            keyboardType="number-pad"
            inputMode="numeric"
            autoComplete="off"
            autoCapitalize="none"
            error={errors.documentNumber}
            testID={`${testIDPrefix}.document-number`}
          />
          {!errors.documentNumber ? (
            <Text
              style={{
                // Pulled back up into the gap the TextInput already reserves so
                // the helper hugs its field (32 - 24 = SPACING.sm), then re-adds
                // the full row gap below itself. Putting it here rather than on
                // the wrapper keeps the gap identical in the error state, where
                // this line is replaced by the input's own error message.
                marginTop: -SPACING.lg,
                marginBottom: FORM_FIELD_GAP,
                fontSize: 12,
                lineHeight: 18,
                fontFamily: FONTS.body,
                color: colors.textMuted,
              }}
            >
              {documentHelper}
            </Text>
          ) : null}
        </View>
      )}

      <View
        style={{
          padding: SPACING.md,
          borderRadius: RADIUS.md,
          backgroundColor: colors.surfaceSubtle,
          borderWidth: 1,
          borderColor: colors.border,
          marginBottom: FORM_FIELD_GAP,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            lineHeight: 18,
            fontFamily: FONTS.body,
            color: colors.textSecondary,
          }}
        >
          {bankCode
            ? t('payouts.review_hint_selected', { bank: getSvBankLabel(bankCode) })
            : t('payouts.review_hint')}
        </Text>
      </View>

      <Button
        title={submitLabel}
        onPress={handleSubmit}
        variant="primary"
        size="lg"
        disabled={!canSubmit}
        loading={submitting}
        testID={`${testIDPrefix}.submit`}
      />
    </View>
  )
}
